// frontend/src/components/Results/ResultsView.js

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import './ResultsStyles.css';
import generarReportePDF from '../../services/reportService';
import ResultsChart from './ResultsChart';
import PerfopRadarChart from './PerfopRadarChart';
import CleaverGraficaIII from './CleaverGraficaIII';
import TermanDetalle from './TermanDetalle';

const PRUEBAS_WIDE = ['CLEAVER', 'PERFOP', 'PERSONALIZADA'];

const ResultsView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const asignacionIdUrl = searchParams.get('asignacion_id');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generandoPdf, setGenerandoPdf] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (id && id !== 'undefined' && id !== 'null') {
      fetchResults();
    } else {
      setError('ID de candidato no válido');
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, asignacionIdUrl]);

  const handleDownloadReport = async () => {
    try {
      setGenerandoPdf(true);
      await generarReportePDF(results, results.candidato);
    } catch (error) {
      console.error('Error al generar reporte:', error);
      alert('Error al generar el reporte PDF');
    } finally {
      setGenerandoPdf(false);
    }
  };

  const fetchResults = async () => {
    try {
      setLoading(true);
      const url = asignacionIdUrl
        ? `/api/resultados/candidato/${id}?asignacion_id=${asignacionIdUrl}`
        : `/api/resultados/candidato/${id}`;
      const response = await fetch(url, {
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`Error ${response.status}`);
      }

      const data = await response.json();
      setResults(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (value) => {
    const num = parseInt(value);
    return isNaN(num) ? 0 : num;
  };

  const calcularPorcentaje = (prueba) => {
    if (['CLEAVER', 'PERFOP', 'PERSONALIZADA'].includes(prueba.prueba_clave)) return null;
    const total = formatNumber(prueba.total_preguntas);
    const correctas = formatNumber(prueba.respuestas_correctas);
    if (total === 0) return 0;
    return Math.round((correctas / total) * 100);
  };

  const getScoreColor = (porcentaje) => {
    if (porcentaje >= 80) return '#2ecc71';
    if (porcentaje >= 60) return '#f39c12';
    return '#e74c3c';
  };

  const clasificacionColors = {
    Alto: '#2ecc71',
    Medio: '#f39c12',
    Bajo: '#e74c3c'
  };

  // ============================================
  // RENDERIZAR CLEAVER (selección forzada M/L/T)
  // ============================================
  const renderCleaverResults = (prueba) => {
    const cleaver = prueba.cleaver;
    if (!cleaver || !cleaver.factores) {
      return (
        <div className="personality-note-card">
          <p>Prueba de personalidad CLEAVER completada</p>
          <small>Preguntas respondidas: {prueba.total_respondidas} de {prueba.total_preguntas}</small>
        </div>
      );
    }

    const dimensionColors = { D: '#e74c3c', I: '#3498db', S: '#2ecc71', C: '#f39c12' };
    const dimensionDescriptions = {
      D: 'Liderazgo, decisión, asertividad',
      I: 'Comunicación, entusiasmo, sociabilidad',
      S: 'Paciencia, consistencia, tranquilidad',
      C: 'Precisión, orden, sistematicidad'
    };

    return (
      <div className="cleaver-results">
        <h4>Perfil de Personalidad — Cleaver (D-I-S-C)</h4>
        <small style={{ color: 'var(--pt-muted)' }}>
          {cleaver.bloques_respondidos} de {cleaver.total_bloques} bloques respondidos
        </small>
        <div className="dimensiones-grid">
          {Object.entries(cleaver.factores).map(([key, factor]) => (
            <div key={key} className="dimension-card" style={{ borderTopColor: dimensionColors[key] }}>
              <div className="dimension-header">
                <span className="dimension-letter" style={{ backgroundColor: dimensionColors[key] }}>{key}</span>
                <span className="dimension-name">{factor.nombre}</span>
              </div>
              <div className="score-bar-container">
                <div className="score-bar" style={{ width: `${factor.t_escalado}%`, backgroundColor: dimensionColors[key] }} />
              </div>
              <span className="score-value">{factor.t_escalado}</span>
              <p className="dimension-desc">{dimensionDescriptions[key]}</p>
              <span className="badge-clasificacion" style={{ backgroundColor: clasificacionColors[factor.clasificacion] || '#999' }}>
                {factor.clasificacion}
              </span>
              <small style={{ display: 'block', marginTop: '6px', color: 'var(--pt-muted)' }}>
                Más: {factor.m} · Menos: {factor.l}
              </small>
            </div>
          ))}
        </div>
        {cleaver.grafica_iii && <CleaverGraficaIII grafica={cleaver.grafica_iii} />}
      </div>
    );
  };

  // ============================================
  // RENDERIZAR PERFOP (Perfil de Personalidad Operativa)
  // ============================================
  const renderPerfopResults = (prueba) => {
    const perfop = prueba.perfop;
    if (!perfop || !perfop.rasgos) {
      return (
        <div className="personality-note-card">
          <p>Prueba PERFOP completada</p>
          <small>Preguntas respondidas: {prueba.total_respondidas} de {prueba.total_preguntas}</small>
        </div>
      );
    }

    return (
      <div className="cleaver-results">
        <h4>Perfil de Personalidad Operativa</h4>
        <small style={{ color: 'var(--pt-muted)' }}>
          {perfop.total_respondidos} de {perfop.total_items} ítems respondidos
        </small>

        <div className="compuestos-row">
          {Object.values(perfop.compuestos).map((c) => (
            <div key={c.nombre} className="compuesto-card">
              <div className="compuesto-valor" style={{ color: clasificacionColors[c.clasificacion] }}>
                {c.decatipo}
              </div>
              <div className="compuesto-nombre">{c.nombre}</div>
              <div className="compuesto-desc">{c.descripcion}</div>
              <span className="badge-clasificacion" style={{ backgroundColor: clasificacionColors[c.clasificacion] }}>
                {c.clasificacion}
              </span>
            </div>
          ))}
        </div>

        <PerfopRadarChart rasgos={perfop.rasgos} />

        <div className="dimensiones-grid">
          {Object.values(perfop.rasgos).map((r) => (
            <div key={r.factor} className="dimension-card">
              <div className="dimension-header">
                <span className="dimension-letter">{r.factor}</span>
                <span className="dimension-name">{r.nombre}</span>
              </div>
              <div className="score-bar-container">
                <div className="score-bar" style={{ width: `${r.decatipo * 10}%`, backgroundColor: 'var(--pt-teal)' }} />
              </div>
              <span className="score-value">{r.decatipo}</span>
              <span className="badge-clasificacion" style={{ backgroundColor: clasificacionColors[r.clasificacion] || '#999' }}>
                {r.clasificacion}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // ============================================
  // RENDERIZAR PRUEBA PERSONALIZADA (creada por la empresa)
  // ============================================
  const renderPersonalizadaResults = (prueba) => {
    const datos = prueba.personalizada;
    if (!datos || !datos.detalle) {
      return (
        <div className="personality-note-card">
          <p>Prueba completada</p>
          <small>Preguntas respondidas: {prueba.total_respondidas} de {prueba.total_preguntas}</small>
        </div>
      );
    }

    return (
      <div className="personalizada-results">
        {datos.tiene_calificacion && (
          <>
            <div className="progress-bar">
              <div
                className="progress-fill"
                style={{ width: `${datos.porcentaje}%`, background: getScoreColor(datos.porcentaje) }}
              >
                {datos.porcentaje}%
              </div>
            </div>
            <div className="stats-row">
              <span>✅ Correctas: {datos.correctas}</span>
              <span>❌ Incorrectas: {datos.total_calificables - datos.correctas}</span>
              <span>📊 Porcentaje: {datos.porcentaje}%</span>
            </div>
          </>
        )}

        <div className="personalizada-preguntas">
          {datos.detalle.map((item) => (
            <div key={item.reactivo_id} className="personalizada-pregunta-card">
              <div className="personalizada-pregunta-header">
                <span className="personalizada-pregunta-numero">Pregunta {item.numero_reactivo}</span>
                {item.calificable && item.respondido && (
                  <span className={item.es_correcta ? 'personalizada-correcta' : 'personalizada-incorrecta'}>
                    {item.es_correcta ? '✅ Correcta' : '❌ Incorrecta'}
                  </span>
                )}
              </div>
              <p className="personalizada-pregunta-texto">{item.texto}</p>
              <p className="personalizada-pregunta-respuesta">
                {!item.respondido ? (
                  <em>Sin responder</em>
                ) : item.tipo_pregunta === 'opcion_multiple' && Array.isArray(item.respuesta) ? (
                  item.respuesta.map(k => item.opciones?.[k] || k).join(', ')
                ) : (item.tipo_pregunta === 'opcion_unica') ? (
                  item.opciones?.[item.respuesta] || item.respuesta
                ) : (
                  item.respuesta
                )}
              </p>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // ============================================
  // RENDERIZAR TERMAN (CI oficial)
  // ============================================
  const renderTermanResults = (prueba) => {
    const terman = prueba.terman;
    if (!terman) return null;

    const rangoColors = {
      'Sobresaliente': '#2ecc71',
      'Superior': '#27ae60',
      'Término Medio Alto': '#3498db',
      'Normal': '#2ecc71',
      'Término Medio Bajo': '#f39c12',
      'Inferior': '#e67e22',
      'Deficiente': '#e74c3c'
    };
    const colorRango = rangoColors[terman.rango_ci] || '#048377';

    return (
      <div className="terman-results" style={{ marginTop: '15px' }}>
        <h4 style={{ margin: '0 0 10px 0', color: 'var(--pt-ink-soft)' }}>Coeficiente Intelectual (Terman-Merril)</h4>
        <div className="metric-row">
          <div className="metric-card">
            <div className="metric-value" style={{ color: colorRango }}>{terman.ci !== null ? terman.ci : '< 69'}</div>
            <div className="metric-label">CI</div>
          </div>
          <div className="metric-card">
            <div className="metric-value" style={{ fontSize: '16px', color: colorRango }}>{terman.rango_ci || 'Sin clasificar'}</div>
            <div className="metric-label">Diagnóstico</div>
          </div>
          {terman.rango_capacidad_aprendizaje && (
            <div className="metric-card">
              <div className="metric-value" style={{ fontSize: '16px', color: 'var(--pt-ink-soft)' }}>{terman.rango_capacidad_aprendizaje}</div>
              <div className="metric-label">Capacidad de aprendizaje</div>
            </div>
          )}
          <div className="metric-card">
            <div className="metric-value" style={{ fontSize: '16px', color: 'var(--pt-ink-soft)' }}>{terman.puntaje_total}</div>
            <div className="metric-label">Puntaje total</div>
          </div>
        </div>

        {terman.interpretacion ? (
          <TermanDetalle interpretacion={terman.interpretacion} />
        ) : terman.series && terman.series.length > 0 && (
          <div className="series-grid">
            {terman.series.map((s) => (
              <div key={s.serie} className="series-card">
                <div style={{ fontSize: '12px', color: 'var(--pt-muted)' }}>Serie {s.serie}</div>
                <div style={{ fontSize: '18px', fontWeight: 'bold', color: 'var(--pt-ink-soft)' }}>{s.puntaje}</div>
                <div style={{ fontSize: '11px', color: 'var(--pt-muted)' }}>{s.aciertos} aciertos / {s.errores} errores</div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  // ============================================
  // RENDER PRINCIPAL
  // ============================================
  if (loading) {
    return (
      <div className="results-loading">
        <div className="spinner"></div>
        <p>Cargando resultados...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="results-error">
        <h2>Error al cargar resultados</h2>
        <p>{error}</p>
        <button onClick={() => navigate('/panel')} className="btn-back">Volver al Dashboard</button>
        <button onClick={fetchResults} className="btn-retry">Reintentar</button>
      </div>
    );
  }

  if (!results) {
    return (
      <div className="results-error">
        <p>No se encontraron resultados</p>
        <button onClick={() => navigate('/panel')} className="btn-back">Volver al Dashboard</button>
      </div>
    );
  }

  const { candidato, estadisticas_globales, resultados, stack, integridad } = results;
  const fechaLarga = (v) => new Date(v).toLocaleDateString('es-MX', { day: 'numeric', month: 'long', year: 'numeric' });
  // Resultados eliminados por la política de retención: solo queda el registro
  const eliminados = results.resultados_eliminados;

  return (
    <div className="results-container">
      <div className="results-topbar">
        <div className="results-topbar-left">
          <button onClick={() => navigate('/panel')} className="btn-back">← Volver</button>
          <div>
            <h1>Resultados de Evaluación</h1>
            {candidato?.nombre_completo && <p className="results-topbar-sub">{candidato.nombre_completo}</p>}
          </div>
        </div>
        <div className="export-actions" style={{ margin: 0 }}>
          <button className="btn-export" onClick={handleDownloadReport} disabled={generandoPdf || Boolean(eliminados)}>
            {generandoPdf ? 'Generando PDF…' : '📥 Descargar Reporte PDF'}
          </button>
          <button className="btn-print" onClick={() => window.print()}>🖨️ Imprimir</button>
        </div>
      </div>

      <div className="results-shell">
        {/* Información del candidato */}
        <div className="candidate-info-results">
          <div className="candidate-avatar">
            {results.foto_url ? (
              <img src={results.foto_url} alt="Foto del candidato" className="candidate-avatar-foto" />
            ) : (
              candidato?.nombre_completo?.charAt(0) || '?'
            )}
          </div>
          <div className="candidate-details">
            <h2>{candidato?.nombre_completo || 'Nombre no disponible'}</h2>
            <div className="candidate-meta">
              <span>📧 {candidato?.email || 'Email no disponible'}</span>
              <span>🏢 {candidato?.empresa || 'Empresa no disponible'}</span>
              <span>🆔 {candidato?.curp || 'CURP no disponible'}</span>
            </div>
          </div>
          {stack && <div className="stack-badge">📦 Stack: {stack}</div>}

          {results.otros_intentos && results.otros_intentos.length > 1 && (
            <div className="stack-badge intentos-selector">
              🔁 Intento:{' '}
              <select
                value={String(results.asignacion_id)}
                onChange={(e) => setSearchParams({ asignacion_id: e.target.value })}
              >
                {results.otros_intentos.map((intento) => (
                  <option key={intento.asignacion_id} value={intento.asignacion_id}>
                    {intento.stack_nombre || 'Stack'} · {new Date(intento.fecha_completada).toLocaleDateString('es-MX')}{intento.resultados_eliminados ? ' (resultados eliminados)' : ''}
                  </option>
                ))}
              </select>
            </div>
          )}

          {resultados && resultados.length > 0 && (
            <div className="stack-chips">
              {resultados.map((p) => {
                const completa = p.total_respondidas >= p.total_preguntas && p.total_preguntas > 0;
                return (
                  <span key={p.prueba_id} className={`stack-chip ${completa ? 'done' : 'pending'}`}>
                    <span className="dot"></span>
                    {p.prueba_nombre} {completa ? '· completada' : `· ${p.total_respondidas}/${p.total_preguntas}`}
                  </span>
                );
              })}
            </div>
          )}
        </div>

        {integridad?.riesgo_fraude && (
          <div className={`integridad-alerta integridad-${integridad.riesgo_fraude}`}>
            <span className="integridad-icono">{integridad.riesgo_fraude === 'rojo' ? '🔴' : '🟡'}</span>
            <div>
              <strong>
                {integridad.riesgo_fraude === 'rojo' ? 'Muy probable' : 'Probable'} indicio de fraude en sus respuestas
              </strong>
              <p>
                Durante las pruebas de habilidad (OTIS/Terman) se detectaron {integridad.cambios_pestana} cambio{integridad.cambios_pestana !== 1 ? 's' : ''} de pestaña
                {integridad.respuestas_rapidas > 0 && ` y ${integridad.respuestas_rapidas} respuesta${integridad.respuestas_rapidas !== 1 ? 's' : ''} contestada${integridad.respuestas_rapidas !== 1 ? 's' : ''} en menos de 2 segundos`}.
                Te recomendamos considerar esto como parte de tu evaluación.
              </p>
            </div>
          </div>
        )}

        {eliminados ? (
          <div className="resultados-eliminados">
            <span aria-hidden="true">🗂️</span>
            <div>
              <strong>Los resultados de esta evaluación ya no están disponibles</strong>
              <p>
                {eliminados.fecha_completada && <>El candidato la terminó el {fechaLarga(eliminados.fecha_completada)}. </>}
                Sus respuestas, puntajes y fotografía se eliminaron el {fechaLarga(eliminados.eliminados_at)} al cumplirse el
                periodo de conservación de resultados. Se conserva solo el registro de que presentó la evaluación.
              </p>
            </div>
          </div>
        ) : (
        <>
        {results.fecha_eliminacion_resultados && (
          <p className="resultados-retencion">
            🗓️ Estos resultados se conservarán hasta el <strong>{fechaLarga(results.fecha_eliminacion_resultados)}</strong>; después se eliminarán automáticamente.
          </p>
        )}

        {/* Estadísticas globales */}
        <div className="stats-cards">
          <div className="stat-card-global">
            <div className="stat-icon">📋</div>
            <div className="stat-value">{formatNumber(estadisticas_globales?.total_pruebas)}</div>
            <div className="stat-label">Pruebas Completadas</div>
          </div>
          <div className="stat-card-global">
            <div className="stat-icon">❓</div>
            <div className="stat-value">{formatNumber(estadisticas_globales?.total_preguntas)}</div>
            <div className="stat-label">Preguntas de Habilidad</div>
          </div>
          <div className="stat-card-global">
            <div className="stat-icon">✅</div>
            <div className="stat-value">{formatNumber(estadisticas_globales?.total_correctas)}</div>
            <div className="stat-label">Respuestas Correctas</div>
          </div>
          <div className="stat-card-global">
            <div className="stat-icon">📈</div>
            <div className="stat-value">{formatNumber(estadisticas_globales?.porcentaje_global)}%</div>
            <div className="stat-label">Rendimiento Global</div>
          </div>
        </div>

        {/* Gráfica de resultados */}
        {resultados && resultados.some(r => (r.prueba_clave === 'TERMAN' || r.prueba_clave === 'OTIS') && r.total_respondidas > 0) && (
          <div className="chart-container">
            <ResultsChart resultados={resultados} />
          </div>
        )}

        {/* Resultados por prueba — solo las pruebas del stack asignado */}
        <div className="results-detailed">
          <h3>Detalle de Resultados</h3>
          {resultados && resultados.length > 0 ? (
            <div className="resultados-lista">
              {resultados.map((prueba, index) => {
                const porcentaje = calcularPorcentaje(prueba);
                const wide = PRUEBAS_WIDE.includes(prueba.prueba_clave);

                return (
                  <div key={index} className={`resultado-item ${wide ? 'wide' : ''}`}>
                    <div className="resultado-header">
                      <h4>{prueba.prueba_nombre}</h4>
                      <span className="prueba-badge">{prueba.prueba_clave}</span>
                    </div>

                    {porcentaje !== null && (
                      <>
                        <div className="progress-bar">
                          <div
                            className="progress-fill"
                            style={{ width: `${porcentaje}%`, background: getScoreColor(porcentaje) }}
                          >
                            {porcentaje}%
                          </div>
                        </div>
                        <div className="stats-row">
                          <span>✅ Correctas: {formatNumber(prueba.respuestas_correctas)}</span>
                          <span>❌ Incorrectas: {formatNumber(prueba.total_preguntas) - formatNumber(prueba.respuestas_correctas)}</span>
                          <span>📊 Porcentaje: {porcentaje}%</span>
                          {prueba.prueba_clave === 'OTIS' && prueba.otis?.diagnostico && (
                            <span>🏷️ Diagnóstico: <strong>{prueba.otis.diagnostico}</strong></span>
                          )}
                        </div>
                      </>
                    )}

                    {prueba.prueba_clave === 'TERMAN' && renderTermanResults(prueba)}
                    {prueba.prueba_clave === 'CLEAVER' && renderCleaverResults(prueba)}
                    {prueba.prueba_clave === 'PERFOP' && renderPerfopResults(prueba)}
                    {prueba.prueba_clave === 'PERSONALIZADA' && renderPersonalizadaResults(prueba)}

                    {!['TERMAN', 'OTIS', 'CLEAVER', 'PERFOP', 'PERSONALIZADA'].includes(prueba.prueba_clave) && (
                      <div className="personality-note-card">
                        <p>Prueba completada</p>
                        <small>Preguntas respondidas: {prueba.total_respondidas} de {prueba.total_preguntas}</small>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <p>No hay resultados disponibles para este candidato.</p>
          )}
        </div>
        </>
        )}
      </div>
    </div>
  );
};

export default ResultsView;
